/**
 * Head-pose estimation from facial landmarks (pipeline stage 4).
 *
 * Two decisions here are worth reading before changing anything.
 *
 * **1. Sides are derived from the image, not from landmark indices.**
 * The estimator takes the two eye-corner landmarks and assigns the one with the smaller
 * image x-coordinate to the model's image-left corner; likewise for the mouth. This is
 * correct for any orientation short of full profile, and past full profile the pose is
 * classified `strongly_away` anyway, where a few degrees of error changes nothing. It also
 * means a mirrored capture cannot silently invert every reported direction.
 *
 * **2. Confidence is computed, not asserted.**
 * `confidence` multiplies three independently meaningful factors: how well the solved pose
 * reprojects onto the measured landmarks, how confident the landmark model was, and how far
 * into the angular range where PnP degrades the solution sits. Any of the three going bad
 * pulls the state toward `unknown`, which is what a product should do instead of reporting a
 * crisp wrong number (§7, §20).
 */

import {
  clamp,
  combineConfidence,
  landmarkPixel,
  type FaceLandmarks,
  type HeadPose,
} from '../models.js';
import {
  MEDIAPIPE_LANDMARK_INDICES,
  MODEL_POINT_ORDER,
  buildIntrinsics,
  eulerFromRotationMatrix,
  projectModelPoints,
  type LandmarkIndexMap,
  type Vec2,
  type Vec3,
} from './geometry.js';
import { solvePnP } from './solvePnP.js';

/** Beyond this yaw/pitch magnitude, six-point PnP loses accuracy noticeably. */
export const ANGLE_CONFIDENCE_FULL_DEGREES = 40;

/**
 * Confidence multiplier at the edge of the measurable range — low, never zero, because a
 * large angle is still real information; it just should not be reported precisely.
 */
export const ANGLE_CONFIDENCE_FLOOR = 0.35;

export interface PoseEstimate {
  readonly pose: HeadPose;
  readonly confidence: number;
  readonly reprojectionErrorPx: number;
  /** Solved head position. Used only for overlay drawing and distance checks; never sent. */
  readonly translationMm: Vec3;
  readonly iterations: number;
}

export interface HeadPoseEstimator {
  estimate(landmarks: FaceLandmarks, detectionConfidence?: number): PoseEstimate | null;
  readonly modelVersion: string;
}

export interface PnPEstimatorOptions {
  landmarkIndices?: LandmarkIndexMap;
  maxReprojectionErrorPx?: number;
  focalLengthScale?: number;
}

export class PnPHeadPoseEstimator implements HeadPoseEstimator {
  private readonly indices: LandmarkIndexMap;
  private readonly maxReprojectionErrorPx: number;
  private readonly focalLengthScale: number;
  /** Reused as the next frame's starting point — consecutive head poses are close. */
  private previous: { rotationVector: Vec3; translation: Vec3 } | null = null;

  constructor(options: PnPEstimatorOptions = {}) {
    this.indices = options.landmarkIndices ?? MEDIAPIPE_LANDMARK_INDICES;
    this.maxReprojectionErrorPx = options.maxReprojectionErrorPx ?? 12;
    this.focalLengthScale = options.focalLengthScale ?? 1;
  }

  get modelVersion(): string {
    return 'pnp-6point-lm/1.0';
  }

  reset(): void {
    this.previous = null;
  }

  estimate(landmarks: FaceLandmarks, detectionConfidence?: number): PoseEstimate | null {
    let imagePoints: Vec2[];
    try {
      imagePoints = this.imagePoints(landmarks);
    } catch {
      // The provider returned fewer landmarks than its index map promises. Unsolvable, not
      // a crash: the run loop must keep going and report `unknown`.
      this.previous = null;
      return null;
    }

    const intrinsics = buildIntrinsics(
      landmarks.imageWidth,
      landmarks.imageHeight,
      this.focalLengthScale,
    );

    const solution = solvePnP(
      imagePoints,
      intrinsics,
      this.previous === null ? {} : { initialGuess: this.previous },
    );

    if (solution === null) {
      // Warm-started solvers can get stuck after a discontinuity (the subject moved
      // sharply, or tracking jumped to another face). Retry once from a cold start before
      // giving up, so one bad frame does not poison the following ones.
      if (this.previous !== null) {
        this.previous = null;
        return this.estimate(landmarks, detectionConfidence);
      }
      return null;
    }

    this.previous = { rotationVector: solution.rotationVector, translation: solution.translation };

    const pose = eulerFromRotationMatrix(solution.rotation);
    const confidence = this.confidence({
      reprojectionErrorPx: solution.reprojectionErrorPx,
      detectionConfidence: detectionConfidence ?? landmarks.detectionConfidence,
      pose,
    });

    return {
      pose,
      confidence,
      reprojectionErrorPx: solution.reprojectionErrorPx,
      translationMm: solution.translation,
      iterations: solution.iterations,
    };
  }

  /** Measured 2D points in `MODEL_POINT_ORDER`, with sides resolved by image x. */
  private imagePoints(landmarks: FaceLandmarks): Vec2[] {
    const nose = landmarkPixel(landmarks, this.indices.noseTip);
    const chin = landmarkPixel(landmarks, this.indices.chin);
    const [eyeA, eyeB] = [
      landmarkPixel(landmarks, this.indices.eyeOuterPair[0]),
      landmarkPixel(landmarks, this.indices.eyeOuterPair[1]),
    ];
    const [mouthA, mouthB] = [
      landmarkPixel(landmarks, this.indices.mouthCornerPair[0]),
      landmarkPixel(landmarks, this.indices.mouthCornerPair[1]),
    ];
    const [eyeLeft, eyeRight] = orderByX(eyeA, eyeB);
    const [mouthLeft, mouthRight] = orderByX(mouthA, mouthB);
    return [nose, chin, eyeLeft, eyeRight, mouthLeft, mouthRight];
  }

  private confidence(input: {
    reprojectionErrorPx: number;
    detectionConfidence: number;
    pose: HeadPose;
  }): number {
    const geometry = clamp(
      1 - input.reprojectionErrorPx / Math.max(this.maxReprojectionErrorPx, 1e-6),
      0,
      1,
    );
    // Geometric mean, not a product — see combineConfidence. A rigid six-point model never
    // fits a real face perfectly, and the misfit grows with angle, so multiplying geometry by
    // the angle penalty double-counts the same underlying effect and buries the reading.
    return combineConfidence(
      geometry,
      angleConfidence(input.pose),
      clamp(input.detectionConfidence, 0, 1),
    );
  }
}

/** Confidence multiplier reflecting PnP degradation at large angles. */
export function angleConfidence(pose: HeadPose): number {
  const magnitude = Math.max(Math.abs(pose.yaw), Math.abs(pose.pitch));
  if (magnitude <= ANGLE_CONFIDENCE_FULL_DEGREES) return 1;
  const span = 90 - ANGLE_CONFIDENCE_FULL_DEGREES;
  const excess = Math.min(magnitude - ANGLE_CONFIDENCE_FULL_DEGREES, span);
  return 1 - (1 - ANGLE_CONFIDENCE_FLOOR) * (excess / span);
}

function orderByX(a: Vec2, b: Vec2): [Vec2, Vec2] {
  return a[0] <= b[0] ? [a, b] : [b, a];
}

// ---------------------------------------------------------------------------------------
// Synthetic input construction (tests, self-test, calibration sanity checks)
// ---------------------------------------------------------------------------------------

/**
 * Wrap projected model points into a `FaceLandmarks` the estimator can consume.
 *
 * A sparse point array is built, populated only at the indices the estimator reads. That
 * keeps the synthetic path honest: it exercises the real index map and the real side
 * resolution rather than a convenient shortcut.
 */
export function landmarksFromProjection(
  projected: readonly Vec2[],
  options: {
    imageWidth: number;
    imageHeight: number;
    detectionConfidence?: number;
    faceCount?: number;
    landmarkIndices?: LandmarkIndexMap;
  },
): FaceLandmarks {
  const {
    imageWidth,
    imageHeight,
    detectionConfidence = 0.95,
    faceCount = 1,
    landmarkIndices = MEDIAPIPE_LANDMARK_INDICES,
  } = options;

  const order = new Map(MODEL_POINT_ORDER.map((name, index) => [name, index]));
  const size =
    Math.max(
      landmarkIndices.noseTip,
      landmarkIndices.chin,
      ...landmarkIndices.eyeOuterPair,
      ...landmarkIndices.mouthCornerPair,
    ) + 1;

  const points: Array<readonly [number, number, number]> = Array.from({ length: size }, () => [
    0, 0, 0,
  ] as const);

  const place = (index: number, xy: Vec2): void => {
    points[index] = [xy[0] / imageWidth, xy[1] / imageHeight, 0] as const;
  };
  const pointAt = (name: (typeof MODEL_POINT_ORDER)[number]): Vec2 =>
    projected[order.get(name) as number] as Vec2;

  place(landmarkIndices.noseTip, pointAt('noseTip'));
  place(landmarkIndices.chin, pointAt('chin'));

  // Assign pair members so image-x order matches the model's left/right.
  const assignPair = (pair: readonly [number, number], left: Vec2, right: Vec2): void => {
    if (left[0] <= right[0]) {
      place(pair[0], left);
      place(pair[1], right);
    } else {
      place(pair[0], right);
      place(pair[1], left);
    }
  };
  assignPair(landmarkIndices.eyeOuterPair, pointAt('eyeImageLeft'), pointAt('eyeImageRight'));
  assignPair(
    landmarkIndices.mouthCornerPair,
    pointAt('mouthImageLeft'),
    pointAt('mouthImageRight'),
  );

  return {
    points,
    imageWidth,
    imageHeight,
    detectionConfidence,
    faceCount,
  };
}

/** Build synthetic landmarks directly from a target pose. */
export function landmarksForPose(
  pose: HeadPose,
  options: {
    imageWidth?: number;
    imageHeight?: number;
    distanceMm?: number;
    detectionConfidence?: number;
    faceCount?: number;
  } = {},
): FaceLandmarks {
  const imageWidth = options.imageWidth ?? 640;
  const imageHeight = options.imageHeight ?? 480;
  const projected = projectModelPoints(pose, {
    imageWidth,
    imageHeight,
    ...(options.distanceMm === undefined ? {} : { distanceMm: options.distanceMm }),
  });
  return landmarksFromProjection(projected, {
    imageWidth,
    imageHeight,
    ...(options.detectionConfidence === undefined
      ? {}
      : { detectionConfidence: options.detectionConfidence }),
    ...(options.faceCount === undefined ? {} : { faceCount: options.faceCount }),
  });
}

export interface PoseSolverSelfTest {
  worstAbsoluteErrorDegrees: number;
  worstCase: { expected: HeadPose; recovered: HeadPose | null };
  samples: number;
  failures: number;
}

/**
 * Validate the pose solver over a grid of known rotations.
 *
 * Runs with no camera and no model file, so it can serve as a startup smoke check on any
 * machine and as the assertion body of the head-pose unit test.
 */
export function selfTestPoseSolver(
  estimator: HeadPoseEstimator = new PnPHeadPoseEstimator(),
  toleranceDegrees = 1,
): PoseSolverSelfTest {
  const angles = [-50, -35, -20, -8, 0, 8, 20, 35, 50];
  let worst = 0;
  let worstCase: { expected: HeadPose; recovered: HeadPose | null } = {
    expected: { yaw: 0, pitch: 0, roll: 0 },
    recovered: null,
  };
  let samples = 0;
  let failures = 0;

  for (const yaw of angles) {
    for (const pitch of angles) {
      const expected: HeadPose = { yaw, pitch, roll: 0 };
      const landmarks = landmarksForPose(expected);
      const estimate = estimator.estimate(landmarks);
      samples += 1;
      if (estimate === null) {
        failures += 1;
        worstCase = { expected, recovered: null };
        worst = Number.POSITIVE_INFINITY;
        continue;
      }
      const error = Math.max(
        Math.abs(estimate.pose.yaw - yaw),
        Math.abs(estimate.pose.pitch - pitch),
        Math.abs(estimate.pose.roll),
      );
      if (error > worst) {
        worst = error;
        worstCase = { expected, recovered: estimate.pose };
      }
      if (error > toleranceDegrees) failures += 1;
    }
  }

  return { worstAbsoluteErrorDegrees: worst, worstCase, samples, failures };
}
